import { z } from 'zod';

import {
  ARTIFACT_STATUS,
  CONFIDENTIALITY,
  CONTEXT_MATURITY_STAGES,
  CONTEXT_TYPES,
  ENTITY_STATUS,
  LENS_MATURITY,
  LENS_VALIDATION_STATUS,
  PRIORITY,
  SAVED_VIEW_ROOT_MODES,
  SAVED_VIEW_SHARE_SCOPES,
  SAVED_VIEW_STATUS,
  VIEW_SEMANTIC_LEVELS,
} from '../constants/enums';

export const projectControlWindowSchema = z.enum(['now', '7d', '30d', 'quarter', 'custom']);
export const projectOperationalStatusSchema = z.enum([
  'proposed',
  'scoped',
  'active',
  'on_hold',
  'closing',
  'completed',
  'cancelled',
  'archived',
]);
export const projectDepthSchema = z.enum(['tracked', 'scoped', 'governed', 'instrumented']);
export const projectHealthStatusSchema = z.enum(['unknown', 'green', 'amber', 'red', 'critical']);
export const projectMovementStatusSchema = z.enum(['improving', 'stable', 'degrading', 'stalled', 'no_signal']);
export const projectReviewFreshnessSchema = z.enum(['fresh', 'due', 'stale', 'missing']);
export const projectControlSurfaceStateSchema = z.enum([
  'loading',
  'empty',
  'normal',
  'stale',
  'overflow',
  'redacted',
  'forbidden',
  'error',
]);
export const projectRecommendationKindSchema = z.enum([
  'add_deliverable',
  'refresh_review',
  'resolve_decision',
  'mitigate_risk',
  'add_measurement',
  'create_saved_view',
]);
export const projectRecommendationSeveritySchema = z.enum(['low', 'medium', 'high', 'critical']);

export const projectControlProjectSchema = z.object({
  id: z.string().min(1),
  company_id: z.string().nullable(),
  name: z.string().min(1),
  record_status: z.enum(ENTITY_STATUS),
  operational_status: projectOperationalStatusSchema,
  description: z.string().nullable(),
  created_at: z.string().min(1),
  updated_at: z.string().min(1),
});

export const projectControlContextSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  context_type: z.enum(CONTEXT_TYPES),
  host_type: z.string().nullable(),
  host_project_id: z.string().min(1),
  maturity_stage: z.enum(CONTEXT_MATURITY_STAGES),
  status: z.string().min(1),
  confidentiality: z.enum(CONFIDENTIALITY),
  review_at: z.string().nullable(),
});

export const projectControlLensSummarySchema = z.object({
  id: z.string().min(1),
  slug: z.string().min(1),
  name: z.string().min(1),
  version: z.string().min(1),
  maturity: z.enum(LENS_MATURITY),
  validation_status: z.enum(LENS_VALIDATION_STATUS),
});

export const projectControlSavedViewSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  lens_id: z.string().min(1),
  lens_version: z.string().min(1),
  root_mode: z.enum(SAVED_VIEW_ROOT_MODES),
  root_context_id: z.string().nullable(),
  semantic_level: z.enum(VIEW_SEMANTIC_LEVELS),
  share_scope: z.enum(SAVED_VIEW_SHARE_SCOPES),
  pinned: z.number().int().min(0).max(1),
  status: z.enum(SAVED_VIEW_STATUS),
});

export const projectControlViewSchema = z.object({
  lens: projectControlLensSummarySchema.nullable(),
  saved_view: projectControlSavedViewSummarySchema.nullable(),
});

export const projectDeliverableSchema = z.object({
  id: z.string().min(1),
  artifact_type: z.enum(['deliverable', 'outcome_record']),
  title: z.string().min(1),
  status: z.enum(ARTIFACT_STATUS),
  priority: z.enum(PRIORITY).nullable(),
  due_date: z.string().nullable(),
  owner_user_id: z.string().nullable(),
  binding_id: z.string().nullable(),
});

export const projectRiskIssueSchema = z.object({
  id: z.string().min(1),
  artifact_type: z.enum(['risk', 'issue']),
  title: z.string().min(1),
  status: z.enum(ARTIFACT_STATUS),
  priority: z.enum(PRIORITY).nullable(),
  due_date: z.string().nullable(),
  owner_user_id: z.string().nullable(),
  binding_id: z.string().nullable(),
});

export const projectMilestoneSchema = z.object({
  id: z.string().min(1),
  artifact_type: z.literal('milestone'),
  title: z.string().min(1),
  status: z.enum(ARTIFACT_STATUS),
  due_date: z.string().nullable(),
  binding_id: z.string().nullable(),
});

export const projectDecisionSummarySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  status: z.string().min(1),
  decision_type: z.string().nullable(),
  artifact_id: z.string().nullable(),
  binding_id: z.string().nullable(),
});

export const projectMetricSummarySchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  unit: z.string().nullable(),
});

export const projectMeasurementSummarySchema = z.object({
  id: z.string().min(1),
  metric_id: z.string().min(1),
  measured_at: z.string().min(1),
  value: z.number(),
  source_system: z.string().nullable(),
  source_artifact_id: z.string().nullable(),
});

export const projectKeyResultSummarySchema = z.object({
  id: z.string().min(1),
  objective_id: z.string().min(1),
  title: z.string().min(1),
  target_value: z.number().nullable(),
  current_value: z.number().nullable(),
  unit: z.string().nullable(),
  metric_id: z.string().nullable(),
  status: z.enum(ENTITY_STATUS),
  metric: projectMetricSummarySchema.nullable(),
  latest_measurement: projectMeasurementSummarySchema.nullable(),
  progress_ratio: z.number().nullable(),
});

export const projectObjectiveSummarySchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().nullable(),
  period: z.string().nullable(),
  owner_user_id: z.string().nullable(),
  status: z.enum(ENTITY_STATUS),
  binding_id: z.string().nullable(),
  key_results: z.array(projectKeyResultSummarySchema),
});

export const projectDependencySchema = z.object({
  id: z.string().min(1),
  binding_id: z.string().min(1),
  subject_type: z.string().min(1),
  subject_id: z.string().min(1),
  relation: z.enum(['requires', 'constrains', 'blocks', 'depends_on']),
  title: z.string().min(1),
  status: z.string().nullable(),
});

export const projectEventSummarySchema = z.object({
  id: z.string().min(1),
  event_type: z.string().min(1),
  status: z.string().min(1),
  source: z.string().min(1),
  subject_type: z.string().nullable(),
  subject_id: z.string().nullable(),
  artifact_id: z.string().nullable(),
  created_at: z.string().min(1),
});

export const projectAgentActivitySchema = z.object({
  id: z.string().min(1),
  artifact_id: z.string().nullable(),
  skill_id: z.string().nullable(),
  status: z.string().min(1),
  output_summary: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
});

export const projectRecommendationSchema = z.object({
  id: z.string().min(1),
  kind: projectRecommendationKindSchema,
  severity: projectRecommendationSeveritySchema,
  title: z.string().min(1),
  subject_type: z.string().min(1),
  subject_id: z.string().min(1),
});

export const projectControlSummarySchema = z.object({
  deliverables: z.number().int().min(0),
  accepted_deliverables: z.number().int().min(0),
  risk_issues: z.number().int().min(0),
  open_risk_issues: z.number().int().min(0),
  milestones: z.number().int().min(0),
  decisions: z.number().int().min(0),
  open_decisions: z.number().int().min(0),
  objectives: z.number().int().min(0),
  key_results: z.number().int().min(0),
  stale_measurements: z.number().int().min(0),
  dependencies: z.number().int().min(0),
  events: z.number().int().min(0),
  agent_runs: z.number().int().min(0),
  recommendations: z.number().int().min(0),
});

export const projectControlTowerPacketSchema = z
  .object({
    packet_type: z.literal('project_control_tower'),
    version: z.literal('v3.project-control-tower.1'),
    generated_at: z.string().min(1),
    window: projectControlWindowSchema,
    surface_state: projectControlSurfaceStateSchema,
    project: projectControlProjectSchema,
    project_context: projectControlContextSchema,
    control_view: projectControlViewSchema,
    depth: projectDepthSchema,
    health_status: projectHealthStatusSchema,
    movement_status: projectMovementStatusSchema,
    review_freshness: projectReviewFreshnessSchema,
    deliverables: z.array(projectDeliverableSchema),
    risks_and_issues: z.array(projectRiskIssueSchema),
    milestones: z.array(projectMilestoneSchema),
    dependencies: z.array(projectDependencySchema),
    decisions: z.array(projectDecisionSummarySchema),
    objectives: z.array(projectObjectiveSummarySchema),
    recent_events: z.array(projectEventSummarySchema),
    agent_activity: z.array(projectAgentActivitySchema),
    recommendation_stream: z.array(projectRecommendationSchema),
    integrity_warnings: z.array(z.string().min(1)),
    summary: projectControlSummarySchema,
  })
  .superRefine((packet, ctx) => {
    if (packet.project_context.context_type !== 'project') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'project context must be context_type project' });
    }
    if (packet.project_context.host_type !== 'project') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'project context must be project-hosted' });
    }
    if (packet.project_context.host_project_id !== packet.project.id) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'project context host must match project' });
    }
    const { lens, saved_view: savedView } = packet.control_view;
    if (savedView) {
      if (savedView.root_mode === 'fixed' && savedView.root_context_id !== packet.project_context.id) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'fixed saved view must root at project context' });
      }
      if (lens && (savedView.lens_id !== lens.id || savedView.lens_version !== lens.version)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'saved view must pin selected lens version' });
      }
    }

    const objectiveIds = new Set(packet.objectives.map((objective) => objective.id));
    for (const objective of packet.objectives) {
      for (const kr of objective.key_results) {
        if (kr.objective_id !== objective.id || !objectiveIds.has(kr.objective_id)) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'key result objective mismatch' });
        }
        if (kr.metric_id !== null && kr.metric?.id !== kr.metric_id) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'key result metric mismatch' });
        }
        if (kr.latest_measurement && kr.latest_measurement.metric_id !== kr.metric_id) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'measurement metric mismatch' });
        }
      }
    }

    const summary = packet.summary;
    const keyResultCount = packet.objectives.reduce((sum, item) => sum + item.key_results.length, 0);
    const openRiskIssueCount = packet.risks_and_issues.filter((item) => !['done', 'cancelled'].includes(item.status)).length;
    const openDecisionCount = packet.decisions.filter((item) => item.status === 'requested').length;
    const staleMeasurementCount = packet.objectives.reduce(
      (sum, objective) => sum + objective.key_results.filter((kr) => !kr.latest_measurement && kr.current_value === null).length,
      0,
    );
    if (
      summary.deliverables !== packet.deliverables.length ||
      summary.accepted_deliverables !== packet.deliverables.filter((item) => item.status === 'done').length ||
      summary.risk_issues !== packet.risks_and_issues.length ||
      summary.open_risk_issues !== openRiskIssueCount ||
      summary.milestones !== packet.milestones.length ||
      summary.decisions !== packet.decisions.length ||
      summary.open_decisions !== openDecisionCount ||
      summary.objectives !== packet.objectives.length ||
      summary.key_results !== keyResultCount ||
      summary.stale_measurements !== staleMeasurementCount ||
      summary.dependencies !== packet.dependencies.length ||
      summary.events !== packet.recent_events.length ||
      summary.agent_runs !== packet.agent_activity.length ||
      summary.recommendations !== packet.recommendation_stream.length
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'summary counts must match packet contents' });
    }
  });

export type ProjectControlTowerPacket = z.infer<typeof projectControlTowerPacketSchema>;
